/**
 * Tour guiado genérico — extraído de usePipelineTour.js (Pipeline) pra ser
 * reaproveitável em qualquer módulo. Mesma API, mas steps e chaves de
 * localStorage vêm por parâmetro em vez de hardcoded, pra dar pra usar em
 * Agenda/Financeiro/GED/etc sem duplicar a lógica.
 *
 * Uso:
 *   const steps = [{ id, target, title, content, position, highlight, showSkip, isLast }, ...]
 *   const tour = useGuidedTour(steps, 'agenda_calendar')
 *   // localStorage keys viram: agenda_calendar_tour_completed / _dismissed
 */

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

export const useGuidedTour = (steps, namespace) => {
  const storageKey = `${namespace}_tour_completed`;
  const dismissedKey = `${namespace}_tour_dismissed`;

  const isActive = ref(false);
  const currentStepIndex = ref(0);
  const targetElement = ref(null);
  const targetRect = ref(null);

  const currentStep = computed(() => steps[currentStepIndex.value] || null);
  const totalSteps = computed(() => steps.length);
  const progress = computed(
    () => ((currentStepIndex.value + 1) / totalSteps.value) * 100
  );
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(
    () => currentStepIndex.value === totalSteps.value - 1
  );

  const shouldShowTour = computed(() => {
    // No celular o tour não começa sozinho (06/10/2026): os passos apontam
    // para o layout do computador e a primeira visita abria com a tela
    // inteira coberta. Pelo botão "?" ele continua disponível.
    if (typeof window !== 'undefined' && window.innerWidth < 640) return false;
    const completed = localStorage.getItem(storageKey) === 'true';
    const dismissed = localStorage.getItem(dismissedKey) === 'true';
    return !completed && !dismissed;
  });

  const updateTargetElement = async () => {
    await nextTick();

    const step = currentStep.value;
    if (!step || !step.target) {
      targetElement.value = null;
      targetRect.value = null;
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      targetElement.value = element;
      targetRect.value = element.getBoundingClientRect();
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      targetElement.value = null;
      targetRect.value = null;
      // eslint-disable-next-line no-console
      console.warn(
        `[useGuidedTour:${namespace}] Target not found for step "${step.id}": ${step.target}. Falling back to center.`
      );
    }
  };

  const endTour = () => {
    isActive.value = false;
    currentStepIndex.value = 0;
    targetElement.value = null;
    targetRect.value = null;
    document.body.classList.remove('tour-active');
  };

  const completeTour = () => {
    localStorage.setItem(storageKey, 'true');
    endTour();
  };

  const skipTour = () => {
    localStorage.setItem(dismissedKey, 'true');
    endTour();
  };

  const startTour = () => {
    currentStepIndex.value = 0;
    isActive.value = true;
    updateTargetElement();
    document.body.classList.add('tour-active');
  };

  const nextStep = () => {
    if (currentStepIndex.value < totalSteps.value - 1) {
      currentStepIndex.value += 1;
      updateTargetElement();
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value -= 1;
      updateTargetElement();
    }
  };

  const goToStep = index => {
    if (index >= 0 && index < totalSteps.value) {
      currentStepIndex.value = index;
      updateTargetElement();
    }
  };

  const resetTour = () => {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(dismissedKey);
  };

  const handleResize = () => {
    if (isActive.value && targetElement.value) {
      targetRect.value = targetElement.value.getBoundingClientRect();
    }
  };

  const handleKeydown = event => {
    if (!isActive.value) return;

    const eventTarget = event.target;
    const isFormField =
      eventTarget instanceof HTMLElement &&
      (eventTarget.tagName === 'INPUT' ||
        eventTarget.tagName === 'TEXTAREA' ||
        eventTarget.tagName === 'SELECT' ||
        eventTarget.isContentEditable);
    if (isFormField) return;

    switch (event.key) {
      case 'Escape':
        skipTour();
        break;
      case 'ArrowRight':
      case 'Enter':
        nextStep();
        break;
      case 'ArrowLeft':
        prevStep();
        break;
      default:
        break;
    }
  };

  onMounted(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('keydown', handleKeydown);
    document.body.classList.remove('tour-active');
  });

  return {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    isFirstStep,
    isLastStep,
    targetElement,
    targetRect,
    shouldShowTour,

    startTour,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    completeTour,
    resetTour,
  };
};

export default useGuidedTour;
